import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SEOHead from '../SEOHead';
import DesktopNavigation from '../Navigation/DesktopNavigation';
import MobileNavigation from '../Navigation/MobileNavigation';
import Footer from '../Footer/Footer';
import OptimizedImage from '../common/OptimizedImage';
import growthProfileBannerImage from '../../assets/growth-profile-updated.webp';

const GrowthTechnologyPage: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans overflow-x-hidden selection:bg-[#05a5b7] selection:text-white flex flex-col">
      <SEOHead 
        title={`${t('growthTechnologyPage.title')} | Christ Reigns Media`}
        description={t('growthTechnologyPage.subtitle')}
      />
      
      <DesktopNavigation />
      <MobileNavigation />
      
      <main className="flex-grow pt-24 xl:pt-32 pb-20">
        <div className="container mx-auto px-6 md:px-8 max-w-4xl">
          
          {/* Header */}
          <div className="border-b border-gray-200 pb-8 mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-4 font-sans leading-tight">
              {t('growthTechnologyPage.title')}
            </h1>
            <p className="text-lg text-[#05a5b7] font-semibold tracking-wide">
              {t('growthTechnologyPage.subtitle')}
            </p>
          </div>

          {/* Top Banner Image */}
          <div className="mb-12 overflow-hidden rounded-3xl border border-gray-200 shadow-md bg-gradient-to-br from-blue-50 to-indigo-100 max-h-[420px]">
            <OptimizedImage
              src={growthProfileBannerImage}
              alt="Growth Profile AI Technology Banner"
              className="w-full h-full object-cover"
              sizes="(max-width: 768px) 100vw, 896px"
            />
          </div>

          {/* Intro Section */}
          <div className="space-y-6 text-lg text-gray-700 leading-relaxed font-light mb-12">
            <p>{t('growthTechnologyPage.introP1')}</p>
            <p>{t('growthTechnologyPage.introP2')}</p>
          </div>

          <div className="space-y-12 text-gray-800 leading-relaxed">
            
            {/* The Two Main Pillars */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                {t('growthTechnologyPage.pillarsTitle')}
              </h2>
              <p>{t('growthTechnologyPage.pillarsIntro')}</p>
              
              <div className="space-y-6 mt-4">
                <div className="bg-gray-50 p-6 rounded-2xl border border-gray-150 shadow-sm">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t('growthTechnologyPage.llamaTitle')}</h3>
                  <p className="text-gray-700 text-base leading-relaxed">{t('growthTechnologyPage.llamaText')}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-2xl border border-gray-150 shadow-sm">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t('growthTechnologyPage.groqTitle')}</h3>
                  <p className="text-gray-700 text-base leading-relaxed">{t('growthTechnologyPage.groqText')}</p>
                </div>
              </div>
            </section>

            {/* Step by Step */}
            <section className="space-y-6">
              <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                {t('growthTechnologyPage.stepTitle')}
              </h2>
              <p>{t('growthTechnologyPage.stepIntro')}</p>

              {/* Step 1 */}
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-gray-900">{t('growthTechnologyPage.step1Title')}</h3>
                <p>{t('growthTechnologyPage.step1Text')}</p>
              </div>

              {/* Step 2 */}
              <div className="space-y-3">
                <h3 className="text-xl font-bold text-gray-900">{t('growthTechnologyPage.step2Title')}</h3>
                <p>{t('growthTechnologyPage.step2Text')}</p>
                <div className="bg-gray-50 border-l-4 border-[#05a5b7] p-5 my-4 rounded-r-xl font-mono text-sm leading-relaxed text-gray-700 shadow-inner">
                  {t('growthTechnologyPage.promptQuote')}
                </div>
                <p>{t('growthTechnologyPage.step2Outro')}</p>
              </div>

              {/* Step 3 */}
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-gray-900">{t('growthTechnologyPage.step3Title')}</h3>
                <p>{t('growthTechnologyPage.step3Text')}</p>
              </div>

              {/* Step 4 */}
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-gray-900">{t('growthTechnologyPage.step4Title')}</h3>
                <p>{t('growthTechnologyPage.step4Text')}</p>
              </div>

              {/* Step 5 */}
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-gray-900">{t('growthTechnologyPage.step5Title')}</h3>
                <p>{t('growthTechnologyPage.step5Text')}</p>
              </div>
            </section>

            {/* Why Is It So Fast and Reliable */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                {t('growthTechnologyPage.whyTitle')}
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>{t('growthTechnologyPage.whySpeed')}</li>
                <li>{t('growthTechnologyPage.whyCost')}</li>
                <li>{t('growthTechnologyPage.whyFaithfulness')}</li>
              </ul>
            </section>

            {/* Limitations */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                {t('growthTechnologyPage.limitsTitle')}
              </h2>
              <p>{t('growthTechnologyPage.limitsIntro')}</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>{t('growthTechnologyPage.limitsPoint1')}</li>
                <li>{t('growthTechnologyPage.limitsPoint2')}</li>
                <li>{t('growthTechnologyPage.limitsPoint3')}</li>
              </ul>
              <p className="pt-2">{t('growthTechnologyPage.limitsOutro')}</p>
            </section>

            {/* Summary */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                {t('growthTechnologyPage.summaryTitle')}
              </h2>
              <p>{t('growthTechnologyPage.summaryIntro')}</p>
              <p>{t('growthTechnologyPage.summaryP1')}</p>
              <p>{t('growthTechnologyPage.summaryP2')}</p>
            </section>

            {/* Callout Footer */}
            <section className="bg-gradient-to-tr from-slate-900 to-[#0c4a6e] text-white p-8 rounded-3xl space-y-4 shadow-xl">
              <p className="text-xl font-bold text-sky-400">
                {t('growthTechnologyPage.summaryP3')}
              </p>
              <p className="text-gray-200 leading-relaxed font-light">
                {t('growthTechnologyPage.summaryP4')}
              </p>
            </section>
          </div>

        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default GrowthTechnologyPage;
